import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import {
  Bell, BellOff, Volume2, VolumeX, Play,
  ShoppingCart, Tag, AlertTriangle, StopCircle, Shield,
  Clock, Zap, Info,
} from "lucide-react";
import { useNotificationSound, EVENT_CONFIG, requestNotificationPermission, type NotificationSettings } from "@/hooks/useNotificationSound";
import { toast } from "sonner";
import { useState, useEffect } from "react";

const EVENT_ICONS: Record<string, any> = {
  trade_buy: ShoppingCart,
  trade_sell: Tag,
  stop_loss_pause: AlertTriangle,
  daily_loss_pause: AlertTriangle,
  engine_pause: StopCircle,
  engine_stop: StopCircle,
  slippage_protect: Shield,
  tail_protect: Clock,
  engine_start: Zap,
  llm_limit: Info,
};

const PRIORITY_COLORS: Record<string, string> = {
  high: "bg-red-500/20 text-red-400",
  medium: "bg-yellow-500/20 text-yellow-400",
  low: "bg-blue-500/20 text-blue-400",
};

export default function NotificationSettingsPanel() {
  const { settings, updateSettings, testSound, initAudio, unreadCount, requestPermission } = useNotificationSound();
  const [notifPermission, setNotifPermission] = useState<string>("default");

  useEffect(() => {
    if ("Notification" in window) {
      setNotifPermission(Notification.permission);
    }
  }, []);

  const handleRequestPermission = async () => {
    const granted = await requestPermission();
    setNotifPermission(granted ? "granted" : "denied");
    if (granted) {
      toast.success("浏览器通知权限已获取");
    } else {
      toast.error("浏览器通知权限被拒绝，请在浏览器设置中手动开启");
    }
  };

  const handleTestSound = (type: "buy" | "sell" | "warning" | "alert" | "info") => {
    initAudio();
    testSound(type);
  };

  return (
    <div className="space-y-4">
      {/* 总开关 */}
      <Card className="border-border/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary" />通知设置
            {unreadCount > 0 && (
              <Badge className="bg-red-500/20 text-red-400 text-[10px]">{unreadCount}条未读</Badge>
            )}
          </CardTitle>
          <CardDescription className="text-[10px]">
            配置交易事件的浏览器通知和提示音提醒
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 主开关 */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {settings.enabled ? <Bell className="h-4 w-4 text-primary" /> : <BellOff className="h-4 w-4 text-muted-foreground" />}
              <Label className="text-sm font-medium">启用通知提醒</Label>
            </div>
            <Switch checked={settings.enabled} onCheckedChange={v => updateSettings({ enabled: v })} />
          </div>

          {settings.enabled && (
            <>
              <Separator />

              {/* 浏览器通知 */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">浏览器弹窗通知</Label>
                  <Switch checked={settings.browserNotification} onCheckedChange={v => updateSettings({ browserNotification: v })} />
                </div>
                {notifPermission !== "granted" && settings.browserNotification && (
                  <Button variant="outline" size="sm" className="w-full text-xs h-7" onClick={handleRequestPermission}>
                    {notifPermission === "denied" ? "通知权限已被拒绝，请在浏览器设置中开启" : "点击授权浏览器通知权限"}
                  </Button>
                )}
                {notifPermission === "granted" && (
                  <div className="text-[10px] text-green-400 flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400" />已获取通知权限
                  </div>
                )}
              </div>

              <Separator />

              {/* 提示音 */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {settings.soundEnabled ? <Volume2 className="h-4 w-4 text-primary" /> : <VolumeX className="h-4 w-4 text-muted-foreground" />}
                    <Label className="text-xs">提示音</Label>
                  </div>
                  <Switch checked={settings.soundEnabled} onCheckedChange={v => updateSettings({ soundEnabled: v })} />
                </div>

                {settings.soundEnabled && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Label className="text-[10px] text-muted-foreground w-8">音量</Label>
                      <Slider
                        value={[settings.volume]}
                        min={0} max={100} step={5}
                        onValueChange={([v]) => updateSettings({ volume: v })}
                        className="flex-1"
                      />
                      <span className="text-[10px] font-mono text-muted-foreground w-8 text-right">{settings.volume}%</span>
                    </div>

                    {/* 试听按钮 */}
                    <div className="flex flex-wrap gap-1.5">
                      {(["buy", "sell", "warning", "alert", "info"] as const).map(type => (
                        <Button key={type} variant="outline" size="sm" className="h-6 text-[10px] px-2"
                          onClick={() => handleTestSound(type)}>
                          <Play className="h-2.5 w-2.5 mr-1" />
                          {type === "buy" ? "买入" : type === "sell" ? "卖出" : type === "warning" ? "警告" : type === "alert" ? "紧急" : "提示"}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* 事件类型开关 */}
              <div className="space-y-2">
                <Label className="text-xs font-medium">事件类型筛选</Label>
                <div className="space-y-1.5">
                  {Object.entries(EVENT_CONFIG).map(([key, config]) => {
                    const Icon = EVENT_ICONS[key] || Info;
                    return (
                      <div key={key} className="flex items-center justify-between py-1">
                        <div className="flex items-center gap-2">
                          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-xs">{config.label}</span>
                          <Badge className={`text-[9px] ${PRIORITY_COLORS[config.priority]}`}>
                            {config.priority === "high" ? "高" : config.priority === "medium" ? "中" : "低"}
                          </Badge>
                        </div>
                        <Switch
                          checked={settings.enabledEvents[key] ?? config.defaultEnabled}
                          onCheckedChange={v => updateSettings({
                            enabledEvents: { ...settings.enabledEvents, [key]: v }
                          })}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
